// TASK-SF-06 -- the interrupted-onboarding draft (02_* §3.7).
//
// sessionStorage ONLY -- never localStorage, never a cookie. It is scoped to
// one key, lives entirely pre-authentication, and self-expires at 900s to
// mirror config.phoneOwnershipGrantTtlSeconds: the registration grant it is
// keyed to cannot outlive that, so neither should the values typed against
// it. Every read and write is wrapped so a storage failure (private mode,
// quota exceeded, storage disabled by policy) is non-fatal -- the wizard
// simply behaves as if there were no draft.
//
// This is the one deliberate, documented exception to LoginView's "no state
// survives refresh" rule (LoginView.vue:22-30).

export const DRAFT_KEY = 'borna.studentOnboarding.v1'

// Mirrors config.phoneOwnershipGrantTtlSeconds (900s). Kept as a literal
// rather than importing config so this util has no module-load side effects
// in a unit test.
export const DRAFT_TTL_MS = 900 * 1000

// Even reading `window.sessionStorage` can throw in a sandboxed iframe or
// when storage is blocked by policy, so acquiring the handle is itself
// guarded.
function storage() {
  try {
    return (typeof window !== 'undefined' && window.sessionStorage) || null
  } catch (e) {
    return null
  }
}

// Persist the wizard's accumulated values against the grant they belong to.
// A missing grantId is a no-op: a draft with nothing to key against could
// only ever be restored onto the wrong phone.
export function saveDraft(grantId, values) {
  if (!grantId) return
  const s = storage()
  if (!s) return
  try {
    s.setItem(
      DRAFT_KEY,
      JSON.stringify({
        grantId,
        savedAt: Date.now(),
        values: values && typeof values === 'object' ? values : {},
      })
    )
  } catch (e) {
    // QuotaExceededError / SecurityError -- non-fatal, the draft is a
    // convenience, not a requirement.
  }
}

// Return the stored values for `grantId`, or null when there is nothing
// usable: no draft, a draft for a different grant, an expired draft, or
// unparseable contents. Any of those also clears the stale entry.
export function loadDraft(grantId) {
  const s = storage()
  if (!s) return null

  let raw
  try {
    raw = s.getItem(DRAFT_KEY)
  } catch (e) {
    return null
  }
  if (!raw) return null

  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    clearDraft()
    return null
  }
  if (!parsed || typeof parsed !== 'object') {
    clearDraft()
    return null
  }

  // A draft for a different grant is stale -- the user re-verified a
  // (possibly different) phone and this belongs to the previous attempt.
  if (grantId && parsed.grantId !== grantId) {
    clearDraft()
    return null
  }

  if (
    typeof parsed.savedAt !== 'number' ||
    Date.now() - parsed.savedAt > DRAFT_TTL_MS
  ) {
    clearDraft()
    return null
  }

  return parsed.values && typeof parsed.values === 'object' ? parsed.values : {}
}

// Cleared on successful registration, on ACCOUNT_CONFLICT / PHONE_CONFLICT,
// and on "اصلاح شماره" (02_* §3.7).
export function clearDraft() {
  const s = storage()
  if (!s) return
  try {
    s.removeItem(DRAFT_KEY)
  } catch (e) {
    // non-fatal
  }
}

export default { DRAFT_KEY, DRAFT_TTL_MS, saveDraft, loadDraft, clearDraft }
